import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout/Layout';
import PublicLayout from '@/components/Layout/PublicLayout';
import LoadingSpinner from '@/components/UI/LoadingSpinner';
import { offlineStorageService } from '@/services/offlineStorageService';
import FirebaseErrorBoundary from '@/components/debug/FirebaseErrorBoundary';
import ErrorBoundary from '@/components/ErrorBoundary';
import ChunkErrorBoundary from '@/components/ChunkErrorBoundary';
import { setupGlobalChunkErrorHandling, withChunkErrorHandling } from '@/utils/chunkErrorHandler';
import { updateHandler } from '@/utils/updateHandler';

// Lazy load pages for better code splitting with chunk error handling
const Dashboard = React.lazy(withChunkErrorHandling(() => import('@/pages/Dashboard')));
const WorkoutView = React.lazy(withChunkErrorHandling(() => import('@/pages/WorkoutView')));
const WorkoutHowTos = React.lazy(withChunkErrorHandling(() => import('@/pages/WorkoutHowTos')));
const WorkoutHowToDetail = React.lazy(withChunkErrorHandling(() => import('@/pages/WorkoutHowToDetail')));
const WearablesPage = React.lazy(withChunkErrorHandling(() => import('@/pages/WearablesPage')));
const Profile = React.lazy(withChunkErrorHandling(() => import('@/pages/Profile')));
const ProfileLayout = React.lazy(withChunkErrorHandling(() => import('@/pages/profile/ProfileLayout')));
const ProfileOverview = React.lazy(withChunkErrorHandling(() => import('@/pages/profile/ProfileOverview')));
const ProfileEdit = React.lazy(withChunkErrorHandling(() => import('@/pages/profile/ProfileEdit')));
const ProfileSubscription = React.lazy(withChunkErrorHandling(() => import('@/pages/profile/ProfileSubscription')));
const Subscription = React.lazy(withChunkErrorHandling(() => import('@/pages/Subscription')));
const ProfileSettings = React.lazy(withChunkErrorHandling(() => import('@/pages/profile/ProfileSettings')));
const ProfileNotifications = React.lazy(withChunkErrorHandling(() => import('@/pages/profile/ProfileNotifications')));
const Progress = React.lazy(withChunkErrorHandling(() => import('@/pages/Progress')));
const Calendar = React.lazy(withChunkErrorHandling(() => import('@/pages/Calendar')));
const WorkoutTemplates = React.lazy(withChunkErrorHandling(() => import('@/pages/WorkoutTemplates')));
const CreateWorkoutTemplate = React.lazy(withChunkErrorHandling(() => import('@/pages/CreateWorkoutTemplate')));
const Login = React.lazy(withChunkErrorHandling(() => import('@/pages/Login')));
const Signup = React.lazy(withChunkErrorHandling(() => import('@/pages/Signup')));
const Onboarding = React.lazy(withChunkErrorHandling(() => import('@/pages/Onboarding')));
const Homepage = React.lazy(withChunkErrorHandling(() => import('@/pages/Homepage')));
const About = React.lazy(withChunkErrorHandling(() => import('@/pages/About')));
const Purpose = React.lazy(withChunkErrorHandling(() => import('@/pages/Purpose')));
const Subscriptions = React.lazy(withChunkErrorHandling(() => import('@/pages/Subscriptions')));
const SubscriptionSuccess = React.lazy(withChunkErrorHandling(() => import('@/pages/SubscriptionSuccess')));
const ApplicationExamples = React.lazy(withChunkErrorHandling(() => import('@/pages/ApplicationExamples')));
const AuthDebug = React.lazy(withChunkErrorHandling(() => import('@/pages/AuthDebug')));
const Social = React.lazy(withChunkErrorHandling(() => import('@/pages/Social')));
const OfflineWorkout = React.lazy(withChunkErrorHandling(() => import('@/pages/OfflineWorkout')));
const AuthActionProxy = React.lazy(withChunkErrorHandling(() => import('@/pages/AuthActionProxy')));
const DynamicHyroxProgram = React.lazy(withChunkErrorHandling(() => import('@/pages/DynamicHyroxProgram')));
const ExtremeTemplates = React.lazy(withChunkErrorHandling(() => import('@/pages/ExtremeTemplates')));
const Recovery = React.lazy(withChunkErrorHandling(() => import('@/pages/Recovery')));
const DataExport = React.lazy(withChunkErrorHandling(() => import('@/pages/DataExport')));

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, userProfile, loading } = useAuth();
  
  if (loading) {
    return <LoadingSpinner fullScreen />;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (!userProfile) {
    return <Navigate to="/onboarding" replace />;
  }
  
  return <>{children}</>;
}

function PublicRoute({ children, redirectIfAuthenticated = false }: { children: React.ReactNode; redirectIfAuthenticated?: boolean }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  if (user && redirectIfAuthenticated) {
    return <Navigate to="/app/dashboard" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Suspense fallback={<LoadingSpinner fullScreen />}>
      <Routes>
        {/* Public routes with public layout */}
        <Route
          path="/"
          element={
            <PublicRoute>
              <PublicLayout />
            </PublicRoute>
          }
        >
          <Route index element={<Homepage />} />
          <Route path="about" element={<About />} />
          <Route path="purpose" element={<Purpose />} />
          <Route path="subscriptions" element={<Subscriptions />} />
          <Route path="subscription/success" element={<SubscriptionSuccess />} />
          <Route path="examples" element={<ApplicationExamples />} />
        </Route>

      {/* Auth routes without layout */}
      <Route
        path="/login"
        element={
          <PublicRoute redirectIfAuthenticated={true}>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <PublicRoute redirectIfAuthenticated={true}>
            <Signup />
          </PublicRoute>
        }
      />

      {/* Onboarding route */}
      <Route path="/onboarding" element={<Onboarding />} />

      {/* Debug route - accessible without authentication */}
      <Route path="/auth-debug" element={<AuthDebug />} />

      {/* Firebase auth action proxy - bypasses our app and redirects to Firebase */}
      <Route path="/__/auth/action" element={<AuthActionProxy />} />

      {/* Protected routes */}
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/app/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="calendar" element={<Calendar />} />
        <Route path="workout/:week/:day" element={<WorkoutView />} />
        <Route path="offline-workout/:workoutId/:day" element={<OfflineWorkout />} />
        <Route path="workout-howtos" element={<WorkoutHowTos />} />
        <Route path="workout-howtos/:exerciseId" element={<WorkoutHowToDetail />} />
        <Route path="wearables" element={<WearablesPage />} />
        <Route path="recovery/*" element={<Recovery />} />
        <Route path="export" element={<DataExport />} />
        <Route path="social" element={<Social />} />
        <Route path="profile" element={<ProfileLayout />}>
          <Route index element={<Navigate to="/app/profile/overview" replace />} />
          <Route path="overview" element={<ProfileOverview />} />
          <Route path="edit" element={<ProfileEdit />} />
          <Route path="subscription" element={<ProfileSubscription />} />
          <Route path="settings" element={<ProfileSettings />} />
          <Route path="notifications" element={<ProfileNotifications />} />
        </Route>
        <Route path="progress" element={<Progress />} />
        <Route path="templates" element={<WorkoutTemplates />} />
        <Route path="templates/create" element={<CreateWorkoutTemplate />} />
        <Route path="templates/extreme" element={<ExtremeTemplates />} />
        <Route path="dynamic-hyrox" element={<DynamicHyroxProgram />} />
        <Route path="subscription" element={<Subscription />} />
      </Route>

      {/* Legacy redirect */}
      <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />

        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  // Initialize offline storage on app start
  useEffect(() => {
    const initializeOfflineStorage = async () => {
      try {
        await offlineStorageService.initialize();
        console.log('Offline storage initialized successfully');
      } catch (error) {
        console.error('Failed to initialize offline storage:', error);
      }
    };

    // Setup global chunk error handling
    setupGlobalChunkErrorHandling();

    // Start update handler to detect new deployments
    updateHandler.start().catch(error => {
      console.warn('Failed to start update handler:', error);
    });

    initializeOfflineStorage();

    // Cleanup on unmount
    return () => {
      updateHandler.stop();
    };
  }, []);

  return (
    <ChunkErrorBoundary>
      <ErrorBoundary>
        <FirebaseErrorBoundary>
          <AuthProvider>
            <Router>
              <div className="min-h-screen bg-gray-50">
                <AppRoutes />
              </div>
            </Router>
          </AuthProvider>
        </FirebaseErrorBoundary>
      </ErrorBoundary>
    </ChunkErrorBoundary>
  );
}

export default App;
